import React, {
  createContext,
  useCallback,
  useContext,
  useDeferredValue,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Input,
  Box,
  Typography,
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  ChipDelete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/joy";
import type { AutocompleteRenderGetTagProps } from "@mui/joy/Autocomplete";
import { useParams } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNameLookup, useNameOptionsQuery } from "../hooks/searchQueries";
import { ActiveCountBadge } from "./ActiveCountBadge";
import { formatAmount, renderPath, toLabel } from "./filterLabels";
import { splitFacets } from "./filterFacets";
import { getDatePresets, getRangePresets } from "./filterPresets";
import { buildFilterIndex, findFilters } from "./filterSearch";
import { splitNameList } from "./nameList";
import {
  activeGroupCounts,
  boundsNode,
  leafKind,
  listNode,
  sortChildren,
  type FilterBounds,
  type FilterValues,
} from "./filterState";

export interface FilterInterface {
  path: string;
  type?: string;
  children?: FilterInterface[];
  allowed_values?: Array<string | number | boolean> | null;
  min?: number | string | null;
  max?: number | string | null;
  /** The index to look this list's values up in as the user types, instead
   *  of listing them all (entity names). */
  lookup?: string | null;
  /** Typed text the value must contain, alongside the picked values. */
  contains?: string[];
}

type FilterStore = {
  values: FilterValues;
  setLeaf: (path: string, node: FilterInterface | null) => void;
  activeCounts: Map<string, number>;
};

const FilterStoreContext = createContext<FilterStore | null>(null);

const useFilterStore = (): FilterStore => {
  const store = useContext(FilterStoreContext);
  if (!store) {
    throw new Error("Filter inputs must be rendered inside FilterUI");
  }
  return store;
};

// Calls the latest `fn` once, `delay` ms after the last call; dropped on unmount.
const useDebouncedCallback = <Args extends unknown[]>(
  fn: (...args: Args) => void,
  delay: number,
) => {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    const timer = timerRef;
    return () => clearTimeout(timer.current);
  }, []);
  return useCallback(
    (...args: Args) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => fnRef.current(...args), delay);
    },
    [delay],
  );
};

const FilterComponent: React.FC<{ filter: FilterInterface }> = ({ filter }) => {
  if (filter.children) return <ObjectFilter filter={filter} />;
  switch (leafKind(filter)) {
    case "list":
      return <ListFilter filter={filter} />;
    case "range":
      return <RangeFilter filter={filter} />;
    case "date":
      return <DateRangeFilter filter={filter} />;
    default:
      return null;
  }
};

const ObjectFilter: React.FC<{ filter: FilterInterface; label?: string }> = ({
  filter,
  label,
}) => {
  const { activeCounts } = useFilterStore();
  const sortedChildren = useMemo(
    () => sortChildren(filter.children),
    [filter.children],
  );

  // Joy's AccordionDetails renders its children even when collapsed (it only
  // sets `hidden`), which mounted every input in the schema on page load. So
  // children mount on first expand and stay mounted. Groups holding an active
  // filter start open, so a shared ?f= link shows where its filters are.
  const activeCount = activeCounts.get(filter.path) ?? 0;
  const [expanded, setExpanded] = useState<boolean>(activeCount > 0);
  const [everExpanded, setEverExpanded] = useState<boolean>(activeCount > 0);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_event, isExpanded) => {
        setExpanded(isExpanded);
        if (isExpanded) setEverExpanded(true);
      }}
      sx={{
        mb: 1,
        '&[aria-expanded="true"]': {
          bgcolor: "background.level2",
        },
      }}
    >
      <AccordionSummary indicator={<ExpandMoreIcon />}>
        <Typography level="title-sm">
          {label ?? renderPath(filter.path)}
        </Typography>
        <ActiveCountBadge count={activeCount} sx={{ ml: 1 }} />
      </AccordionSummary>
      <AccordionDetails
        sx={{ pl: 1.5, borderLeft: "2px solid", borderColor: "divider" }}
      >
        {everExpanded &&
          sortedChildren.map((child, idx) => (
            <FilterComponent key={idx} filter={child} />
          ))}
      </AccordionDetails>
    </Accordion>
  );
};

type ListValue = string | number | boolean;

// Lists up to this long show every option as a checkbox. Longer ones, and
// lists that take typed values, get a searchable dropdown.
const CHECKBOX_LIST_MAX = 10;
// A longer checkbox list shows this many options until "Show more".
const CHECKBOX_FOLD = 5;

const BOOLEAN_CHOICES = ["Any", "Yes", "No"] as const;

// Any / Yes / No as one segmented control; "Any" clears the filter.
const BooleanChoice: React.FC<{
  labelId: string;
  committed: ListValue[] | null | undefined;
  onPick: (value: boolean | null) => void;
}> = ({ labelId, committed, onPick }) => {
  const choice = committed?.length === 1 ? toLabel(committed[0], true) : "Any";
  return (
    <RadioGroup
      orientation="horizontal"
      aria-labelledby={labelId}
      value={choice}
      onChange={(event) => {
        const next = event.target.value;
        onPick(next === "Any" ? null : next === "Yes");
      }}
      size="sm"
      sx={{
        p: 0.5,
        borderRadius: "md",
        bgcolor: "neutral.softBg",
        "--RadioGroup-gap": "4px",
        "--Radio-actionRadius": "6px",
      }}
    >
      {BOOLEAN_CHOICES.map((item) => (
        <Radio
          key={item}
          value={item}
          label={item}
          disableIcon
          variant="plain"
          color="neutral"
          sx={{ flex: 1, justifyContent: "center", py: 0.5 }}
          slotProps={{
            action: ({ checked }) => ({
              sx: checked
                ? {
                    bgcolor: "background.surface",
                    boxShadow: "sm",
                    "&:hover": { bgcolor: "background.surface" },
                  }
                : {},
            }),
          }}
        />
      ))}
    </RadioGroup>
  );
};

const CheckboxList: React.FC<{
  labelId: string;
  options: string[];
  selected: string[];
  onChange: (labels: string[]) => void;
}> = ({ labelId, options, selected, onChange }) => {
  const [expanded, setExpanded] = useState(false);
  const picked = new Set(selected);
  // Folding away a single option saves nothing.
  const folds = options.length > CHECKBOX_FOLD + 1;
  // Picked options past the fold still show, so a selection is never hidden.
  const shown =
    folds && !expanded
      ? options.filter(
          (option, index) => index < CHECKBOX_FOLD || picked.has(option),
        )
      : options;
  const hiddenCount = options.length - shown.length;

  return (
    <div role="group" aria-labelledby={labelId}>
      <Stack spacing={0.75}>
        {shown.map((option) => (
          <Checkbox
            key={option}
            size="sm"
            label={option}
            checked={picked.has(option)}
            onChange={(event) =>
              onChange(
                event.target.checked
                  ? [...selected, option]
                  : selected.filter((value) => value !== option),
              )
            }
          />
        ))}
      </Stack>
      {folds && (expanded || hiddenCount > 0) && (
        <Button
          variant="plain"
          size="sm"
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
          sx={{ mt: 0.5, px: 0.5 }}
        >
          {expanded ? "Show fewer" : `Show ${hiddenCount} more`}
        </Button>
      )}
    </div>
  );
};

const renderPickedChips = (
  tags: string[],
  getTagProps: AutocompleteRenderGetTagProps,
) =>
  tags.map((option, index) => {
    const { key, ...deleteProps } = getTagProps({ index });
    // A long name truncates rather than pushing the X out of the rail; the
    // full name shows on hover.
    return (
      <Chip
        key={key}
        variant="solid"
        color="primary"
        title={option}
        sx={{ minWidth: 0, maxWidth: "100%" }}
        endDecorator={
          <ChipDelete {...deleteProps} aria-label={`Remove ${option}`} />
        }
      >
        {option}
      </Chip>
    );
  });

// A dropdown lists at most this many options. Rendering every match of a long
// list on each keystroke is what made big dropdowns lag.
const MAX_LISTED_OPTIONS = 50;

const firstMatches = (
  options: string[],
  { inputValue }: { inputValue: string },
): string[] => {
  const needle = inputValue.trim().toLowerCase();
  const matches: string[] = [];
  for (const option of options) {
    if (needle === "" || option.toLowerCase().includes(needle)) {
      matches.push(option);
      if (matches.length === MAX_LISTED_OPTIONS) break;
    }
  }
  return matches;
};

// Picked values sit at the top of the list. The order is set when the list
// opens, so options don't jump about while someone is picking.
const SearchableList: React.FC<{
  labelId: string;
  options: string[];
  selected: string[];
  freeSolo: boolean;
  onChange: (labels: string[]) => void;
}> = ({ labelId, options, selected, freeSolo, onChange }) => {
  const [pinned, setPinned] = useState<string[]>([]);
  const ordered = useMemo(() => {
    const top = new Set(pinned);
    if (top.size === 0) return options;
    return [
      ...options.filter((option) => top.has(option)),
      ...options.filter((option) => !top.has(option)),
    ];
  }, [options, pinned]);

  return (
    <Autocomplete
      multiple
      placeholder="Search values…"
      options={ordered}
      filterOptions={firstMatches}
      value={selected}
      freeSolo={freeSolo}
      disableCloseOnSelect
      onOpen={() => setPinned(selected)}
      onChange={(_, newValue) => onChange(newValue.map(String))}
      slotProps={{ input: { "aria-labelledby": labelId } }}
      sx={{
        width: "100%",
        "--Input-minHeight": "20px",
      }}
      renderTags={renderPickedChips}
    />
  );
};

// The value `delay` ms after it last changed.
const useDebouncedValue = <T,>(value: T, delay: number): T => {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return settled;
};

const NAME_LOOKUP_DELAY_MS = 250;

// A dropdown whose options come from the server as the user types, for lists
// too long to send whole (entity names).
const NO_TERMS: string[] = [];

// A picked name matches exactly; typed text matches every name containing it.
type LookupItem = { label: string; contains: boolean };

const sameItem = (a: LookupItem, b: LookupItem) =>
  a.label === b.label && a.contains === b.contains;

const listSize = (text: string) => splitNameList(text).length;

const chipText = (item: LookupItem) =>
  item.contains ? `contains “${item.label}”` : item.label;

// The server reads at most this many "contains" terms per filter
// (MAX_CONTAINS_TERMS) and drops the rest without saying so.
const MAX_CONTAINS_TERMS = 10;
// The filter travels in the URL, so a list of names can't grow without bound.
const MAX_LISTED_NAMES = 100;
const NAME_LOOKUP_BATCH = 5;

const LookupList: React.FC<{
  labelId: string;
  index: string;
  selected: string[];
  contains: string[];
  onChange: (labels: string[], contains: string[]) => void;
}> = ({ labelId, index, selected, contains, onChange }) => {
  const { workspace_id } = useParams() as { workspace_id?: string };
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const typed = input.trim();
  const query = useDebouncedValue(typed, NAME_LOOKUP_DELAY_MS);
  const lookup = useNameOptionsQuery(workspace_id ?? "", index, query, open);
  const lookupName = useNameLookup(workspace_id ?? "", index);
  // How many lists of names are being looked up.
  const [resolving, setResolving] = useState(0);
  const [pasteNote, setPasteNote] = useState("");

  const value = useMemo<LookupItem[]>(
    () => [
      ...selected.map((label) => ({ label, contains: false })),
      ...contains.map((label) => ({ label, contains: true })),
    ],
    [selected, contains],
  );
  // While there's text, the first option offers it as a "contains" match.
  const options = useMemo<LookupItem[]>(
    () => [
      ...(typed ? [{ label: typed, contains: true }] : []),
      ...(lookup.data ?? []).map((label) => ({ label, contains: false })),
    ],
    [typed, lookup.data],
  );

  const commit = (items: Array<LookupItem | string>) => {
    // Enter on typed text arrives as a plain string: a "contains" match.
    const next = items
      .map((item) =>
        typeof item === "string"
          ? { label: item.trim(), contains: true }
          : item,
      )
      .filter(
        (item, i, all) =>
          item.label !== "" &&
          all.findIndex((other) => sameItem(other, item)) === i,
      );
    onChange(
      next.filter((item) => !item.contains).map((item) => item.label),
      next.filter((item) => item.contains).map((item) => item.label),
    );
  };

  // Chips can change while a list of names is being looked up.
  const latestValue = useRef(value);
  latestValue.current = value;

  // A pasted or typed list adds one chip per name: the exact name where the
  // index has it, otherwise a "contains" match.
  const addNames = async (listed: string[]) => {
    const names = listed.slice(0, MAX_LISTED_NAMES);
    setResolving((count) => count + 1);
    // A list still being looked up keeps its note.
    if (resolving === 0) setPasteNote("");
    const resolved: LookupItem[] = [];
    for (let i = 0; i < names.length; i += NAME_LOOKUP_BATCH) {
      const batch = names.slice(i, i + NAME_LOOKUP_BATCH);
      const found = await Promise.all(
        batch.map((name) => lookupName(name).catch(() => [] as string[])),
      );
      batch.forEach((name, j) => {
        const exact = found[j].find(
          (option) => option.toLowerCase() === name.toLowerCase(),
        );
        resolved.push(
          exact
            ? { label: exact, contains: false }
            : { label: name, contains: true },
        );
      });
    }

    const current = latestValue.current;
    let room =
      MAX_CONTAINS_TERMS - current.filter((item) => item.contains).length;
    const added: LookupItem[] = [];
    const skipped: string[] = [];
    for (const item of resolved) {
      if (current.some((other) => sameItem(other, item))) continue;
      if (!item.contains) added.push(item);
      else if (room > 0) {
        added.push(item);
        room -= 1;
      } else skipped.push(item.label);
    }
    // A second list can finish before this one's chips have rendered, so it
    // has to see them here.
    latestValue.current = [...current, ...added];
    commit(latestValue.current);

    const notes: string[] = [];
    if (skipped.length) {
      notes.push(
        `Not added: ${skipped.join(", ")}. These aren't exact names, and a filter takes at most ${MAX_CONTAINS_TERMS} “contains” matches.`,
      );
    }
    if (listed.length > names.length) {
      notes.push(
        `Only the first ${MAX_LISTED_NAMES} of ${listed.length} names were read.`,
      );
    }
    if (notes.length) {
      setPasteNote((note) => [note, ...notes].filter(Boolean).join(" "));
    }
    setResolving((count) => count - 1);
  };

  return (
    <>
      <Autocomplete
        multiple
        freeSolo
        placeholder="Type a name, or paste a list…"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={options}
        // The server already matched them.
        filterOptions={(found) => found}
        getOptionLabel={(item) =>
          typeof item === "string"
            ? item
            : item.contains
              ? listSize(item.label) > 1
                ? `Add ${listSize(item.label)} names`
                : `Names containing “${item.label}”`
              : item.label
        }
        isOptionEqualToValue={sameItem}
        loading={lookup.isFetching || resolving > 0}
        inputValue={input}
        onInputChange={(_, next) => setInput(next)}
        onPaste={(event: React.ClipboardEvent) => {
          const names = splitNameList(event.clipboardData.getData("text"));
          // A single name is pasted as typed text, to search with.
          if (names.length < 2) return;
          event.preventDefault();
          // The list replaces whatever was being typed; left in the box, that
          // text would become a chip of its own on blur.
          setInput("");
          void addNames(names);
        }}
        value={value}
        disableCloseOnSelect
        onChange={(_, newValue) => {
          // Typed text holding several names is read as a pasted list is.
          const list = newValue
            .map((item) => (typeof item === "string" ? item : item.label))
            .find(
              (label, i) =>
                listSize(label) > 1 &&
                !value.some((item) => item.label === label) &&
                (typeof newValue[i] === "string" ||
                  (newValue[i] as LookupItem).contains),
            );
          if (list === undefined) return commit(newValue);
          setInput("");
          void addNames(splitNameList(list));
        }}
        // Leaving the field with text in it keeps that text as a match.
        onBlur={() => {
          if (!typed) return;
          if (listSize(typed) > 1) void addNames(splitNameList(typed));
          else commit([...value, typed]);
          setInput("");
        }}
        slotProps={{ input: { "aria-labelledby": labelId } }}
        sx={{
          width: "100%",
          "--Input-minHeight": "20px",
        }}
        renderTags={(tags, getTagProps) =>
          renderPickedChips(
            tags.map((tag) => (typeof tag === "string" ? tag : chipText(tag))),
            getTagProps,
          )
        }
      />
      {pasteNote && (
        <Typography
          level="body-xs"
          color="warning"
          role="status"
          sx={{ mt: 0.5 }}
        >
          {pasteNote}
        </Typography>
      )}
    </>
  );
};

const ListFilter: React.FC<{ filter: FilterInterface }> = ({ filter }) => {
  const { values, setLeaf } = useFilterStore();
  const labelId = useId();
  const allowedValues = filter.allowed_values;

  // Each option's label, in schema order, and the value it stands for.
  const valueLookup = useMemo(() => {
    const map = new Map<string, ListValue>();
    (allowedValues ?? []).forEach((value) => {
      map.set(toLabel(value, false), value);
    });
    return map;
  }, [allowedValues]);
  const options = useMemo(() => Array.from(valueLookup.keys()), [valueLookup]);

  const committed = values[filter.path]?.allowed_values;
  const containsTerms = values[filter.path]?.contains ?? NO_TERMS;
  const selected = useMemo(
    () => (committed ?? []).map((value) => toLabel(value, false)),
    [committed],
  );

  const pick = (picked: ListValue[]) =>
    setLeaf(filter.path, listNode(filter, picked));
  const select = (labels: string[]) =>
    pick(labels.map((label) => valueLookup.get(label) ?? label));

  const allowFreeSolo = !options.length || filter.path.includes(".name.");

  let control: React.ReactNode;
  if (filter.type === "boolean") {
    control = (
      <BooleanChoice
        labelId={labelId}
        committed={committed}
        onPick={(value) => pick(value === null ? [] : [value])}
      />
    );
  } else if (filter.lookup) {
    control = (
      <LookupList
        labelId={labelId}
        index={filter.lookup}
        selected={selected}
        contains={containsTerms}
        onChange={(labels, contains) =>
          setLeaf(filter.path, listNode(filter, labels, contains))
        }
      />
    );
  } else if (!allowFreeSolo && options.length <= CHECKBOX_LIST_MAX) {
    control = (
      <CheckboxList
        labelId={labelId}
        options={options}
        selected={selected}
        onChange={select}
      />
    );
  } else {
    control = (
      <SearchableList
        labelId={labelId}
        options={options}
        selected={selected}
        freeSolo={allowFreeSolo}
        onChange={select}
      />
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography id={labelId} level="title-sm" sx={{ mb: 0.5 }}>
        {renderPath(filter.path)}
      </Typography>
      {control}
      {filter.path?.includes("internal_dev_capabilities") && (
        <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
          In-house capabilities such as incubation support, R&D development,
          functional expertise, or back-office operations.
        </Typography>
      )}
    </Box>
  );
};

const normalizeNumber = (input: FilterInterface["min"]) => {
  if (input === null || input === undefined) return undefined;
  if (typeof input === "number") return input;
  const parsed = Number(input);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const parseInput = (input: string) => {
  if (input === "") return undefined;
  const parsed = Number(input);
  return Number.isNaN(parsed) ? undefined : parsed;
};

type BoundsDraft = { min: string; max: string };

const draftFrom = (bounds: FilterBounds | undefined): BoundsDraft => ({
  min: normalizeNumber(bounds?.min)?.toString() ?? "",
  max: normalizeNumber(bounds?.max)?.toString() ?? "",
});

const sameBounds = (a: FilterBounds, b: FilterBounds | undefined) =>
  normalizeNumber(a.min) === normalizeNumber(b?.min) &&
  normalizeNumber(a.max) === normalizeNumber(b?.max);

// Min and max boxes, plus one-click presets where the field has them.
const RangeFilter: React.FC<{ filter: FilterInterface }> = ({ filter }) => {
  const { values, setLeaf } = useFilterStore();
  const presets = useMemo(() => getRangePresets(filter.path), [filter.path]);
  const committed = values[filter.path];
  const floor = normalizeNumber(filter.min);
  const ceiling = normalizeNumber(filter.max);
  const title = renderPath(filter.path);

  // A bound equal to the schema's own adds nothing, so it's left out. With
  // neither bound left there is no filter.
  const toBounds = useCallback(
    (draft: BoundsDraft): FilterBounds => {
      const bounds: FilterBounds = {};
      const min = parseInput(draft.min);
      const max = parseInput(draft.max);
      if (min !== undefined && min !== floor) bounds.min = min;
      if (max !== undefined && max !== ceiling) bounds.max = max;
      return bounds;
    },
    [floor, ceiling],
  );

  // A local draft so typing isn't interrupted; it commits 300 ms after the last edit.
  const [draft, setDraft] = useState<BoundsDraft>(() => draftFrom(committed));
  // Follow outside changes (a removed chip, Clear all), but keep what was
  // typed when it already means the committed filter, e.g. a min at the floor.
  useEffect(() => {
    setDraft((current) =>
      sameBounds(toBounds(current), committed) ? current : draftFrom(committed),
    );
  }, [committed, toBounds]);

  const commit = (next: BoundsDraft) => {
    const bounds = toBounds(next);
    const empty = bounds.min === undefined && bounds.max === undefined;
    setLeaf(filter.path, empty ? null : boundsNode(filter, bounds));
  };
  const commitLater = useDebouncedCallback(commit, 300);

  const update = (next: BoundsDraft) => {
    setDraft(next);
    commitLater(next);
  };

  const current = toBounds(draft);
  const activePreset = presets.find((preset) =>
    sameBounds(toBounds(draftFrom(preset)), current),
  )?.label;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography level="title-sm" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Input
          type="number"
          value={draft.min}
          onChange={(event) => update({ ...draft, min: event.target.value })}
          placeholder={
            floor === undefined
              ? "Min"
              : `Min ${formatAmount(floor, filter.path)}`
          }
          slotProps={{ input: { "aria-label": `${title}, minimum` } }}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <Typography level="body-sm">to</Typography>
        <Input
          type="number"
          value={draft.max}
          onChange={(event) => update({ ...draft, max: event.target.value })}
          placeholder={
            ceiling === undefined
              ? "Max"
              : `Max ${formatAmount(ceiling, filter.path)}`
          }
          slotProps={{ input: { "aria-label": `${title}, maximum` } }}
          sx={{ flex: 1, minWidth: 0 }}
        />
      </Box>
      {presets.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1 }}
        >
          {presets.map((preset) => {
            const isSelected = activePreset === preset.label;
            return (
              <Chip
                key={preset.label}
                size="sm"
                variant={isSelected ? "solid" : "outlined"}
                color="primary"
                slotProps={{ action: { "aria-pressed": isSelected } }}
                onClick={() =>
                  update(isSelected ? { min: "", max: "" } : draftFrom(preset))
                }
              >
                {preset.label}
              </Chip>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

const normalizeDateInput = (value: FilterInterface["min"]) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") {
    return value.length >= 10 ? value.slice(0, 10) : value;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().split("T")[0];
};

// Same decorator width for "From" and "To", so the two dates line up.
const dateInputSx = { "& .MuiInput-startDecorator": { minWidth: "2.5rem" } };

const DateRangeFilter: React.FC<{ filter: FilterInterface }> = ({ filter }) => {
  const { values, setLeaf } = useFilterStore();
  const min = normalizeDateInput(filter.min);
  const max = normalizeDateInput(filter.max);
  const committed = values[filter.path];
  const startDate = normalizeDateInput(committed?.min) || min;
  const endDate = normalizeDateInput(committed?.max) || max;
  const title = renderPath(filter.path);
  // Relative to the day the filter mounted.
  const presets = useMemo(() => getDatePresets(new Date()), []);

  const commit = (start: string, end: string) => {
    if (start === min && end === max) {
      setLeaf(filter.path, null);
      return;
    }
    const bounds: FilterBounds = {};
    if (start !== "" && start !== min) bounds.min = start;
    if (end !== "" && end !== max) bounds.max = end;
    setLeaf(filter.path, boundsNode(filter, bounds));
  };

  // Presets run to today, so one is only active while no end date is set.
  const openEnded = normalizeDateInput(committed?.max) === "";
  const activePreset =
    committed && openEnded
      ? presets.find(
          (preset) => preset.min === normalizeDateInput(committed.min),
        )?.label
      : undefined;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography level="title-sm" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      {/* Stacked, since two date inputs side by side don't fit the rail. */}
      <Stack spacing={1}>
        <Input
          type="date"
          value={startDate}
          onChange={(e) => commit(e.target.value, endDate)}
          startDecorator="From"
          slotProps={{ input: { "aria-label": `${title}, from` } }}
          sx={dateInputSx}
          placeholder="Start date"
        />
        <Input
          type="date"
          value={endDate}
          onChange={(e) => commit(startDate, e.target.value)}
          startDecorator="To"
          slotProps={{ input: { "aria-label": `${title}, to` } }}
          sx={dateInputSx}
          placeholder="End date"
        />
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 1 }}
      >
        {presets.map((preset) => {
          const isSelected = activePreset === preset.label;
          return (
            <Chip
              key={preset.label}
              size="sm"
              variant={isSelected ? "solid" : "outlined"}
              color="primary"
              slotProps={{ action: { "aria-pressed": isSelected } }}
              onClick={() => commit(isSelected ? min : preset.min, max)}
            >
              {preset.label}
            </Chip>
          );
        })}
      </Stack>
    </Box>
  );
};

// Enough to find something; more would mount a long list of inputs per keystroke.
const MAX_MATCHES = 20;

const describeValues = (values: string[]): string => {
  const shown = values
    .slice(0, 2)
    .map((value) => `“${value}”`)
    .join(", ");
  const more = values.length - 2;
  return more > 0 ? `Has ${shown} and ${more} more` : `Has ${shown}`;
};

const matchSummary = (shown: number, total: number, search: string) => {
  if (total === 0) return `No filters match “${search}”.`;
  if (shown < total) {
    return `Showing ${shown} of ${total} matching filters. Keep typing to narrow it down.`;
  }
  return `${total} matching filter${total === 1 ? "" : "s"}`;
};

// The filters matching a "Find a filter…" search, as live inputs, each under
// the groups it sits in and the options that matched.
const FilterMatches: React.FC<{
  filterData: FilterInterface;
  search: string;
}> = ({ filterData, search }) => {
  const index = useMemo(() => buildFilterIndex(filterData), [filterData]);
  const { matches, total } = useMemo(
    () => findFilters(index, search, MAX_MATCHES),
    [index, search],
  );

  return (
    <>
      <Typography
        role="status"
        level="body-sm"
        color="neutral"
        sx={{ mb: 1.5 }}
      >
        {matchSummary(matches.length, total, search)}
      </Typography>
      {matches.length > 0 && (
        <Box
          component="ul"
          aria-label="Matching filters"
          sx={{ listStyle: "none", m: 0, p: 0 }}
        >
          {matches.map(({ filter, groups, values }) => {
            const context = [
              groups.join(" › "),
              values.length ? describeValues(values) : "",
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <li key={filter.path}>
                {context && (
                  <Typography level="body-xs" color="neutral">
                    {context}
                  </Typography>
                )}
                <FilterComponent filter={filter} />
              </li>
            );
          })}
        </Box>
      )}
    </>
  );
};

type FilterUIProps = {
  filterData: FilterInterface;
  value: FilterValues;
  onChange: (next: FilterValues) => void;
  /** Leaf paths shown up front; the rest go behind "More filters". */
  primaryFacets?: readonly string[];
  /** Text from the "Find a filter…" box; while set, matches replace the tree. */
  search?: string;
};

const NO_FACETS: readonly string[] = [];

// Controlled: the parent owns `value` and derives the filter doc from it with
// buildFilterDoc(filterData, value).
export const FilterUI = React.memo(function FilterUI({
  filterData,
  value,
  onChange,
  primaryFacets = NO_FACETS,
  search = "",
}: FilterUIProps) {
  // Debounced inputs commit after their render, so always read the newest map.
  const latest = useRef({ value, onChange });
  latest.current = { value, onChange };

  const setLeaf = useCallback((path: string, node: FilterInterface | null) => {
    const { value: current, onChange: notify } = latest.current;
    if (node === null && !(path in current)) return;
    const next = { ...current };
    if (node === null) delete next[path];
    else next[path] = node;
    latest.current = { value: next, onChange: notify };
    notify(next);
  }, []);

  const { primary, more } = useMemo(
    () => splitFacets(filterData, primaryFacets),
    [filterData, primaryFacets],
  );

  // Counted over the tree as shown, so a group under "More filters" doesn't
  // count a leaf that was lifted out of it to the top.
  const activeCounts = useMemo(
    () => (more ? activeGroupCounts(more, value) : new Map<string, number>()),
    [more, value],
  );
  const store = useMemo(
    () => ({ values: value, setLeaf, activeCounts }),
    [value, setLeaf, activeCounts],
  );

  // The rail or drawer around this is already titled "Filters", so the root
  // isn't a group of its own: its children are the top level.
  const topLevel = useMemo(
    () => (more?.children ? sortChildren(more.children) : more ? [more] : []),
    [more],
  );

  // Typing stays responsive while the matching inputs mount.
  const deferredSearch = useDeferredValue(search.trim());
  const searching = deferredSearch !== "";

  return (
    <FilterStoreContext.Provider value={store}>
      {searching && (
        <FilterMatches filterData={filterData} search={deferredSearch} />
      )}
      {/* Hidden rather than unmounted while searching, so open groups stay open. */}
      <div hidden={searching}>
        {primary.map((filter) => (
          <FilterComponent key={filter.path} filter={filter} />
        ))}
        {primary.length > 0 && more?.children ? (
          <ObjectFilter filter={more} label="More filters" />
        ) : (
          topLevel.map((filter) => (
            <FilterComponent key={filter.path} filter={filter} />
          ))
        )}
      </div>
    </FilterStoreContext.Provider>
  );
});
